// hooks/modals/useClientCountryManagement.ts
import { useCallback, useMemo } from 'react';
import type { AuthUser } from '@/types/auth';
import type { Country } from '@/types/countries';

interface EntityData {
    id?: number;
    country_code?: string;
    country_list?: string[];
    currency_code?: string;
    operational_countries?: Array<{ country_code: string }>;
    [key: string]: unknown;
}

interface CountryPermissions {
    isSuperUser: boolean;
    isCustomerUser: boolean;
    isEditingOwnRecord: boolean;
    canEditCountries: boolean;
    availableCountries: string[];
}

interface CountryValidationResult {
    isValid: boolean;
    errors: Record<string, string>;
}

export const useClientCountryManagement = (
    user: AuthUser | null | undefined,
    entityData: EntityData | null = {},
    countries: Country[] = []
) => {
    // isSuperUser, not role === 'ROLE_ADMIN': the flag is what useEffectiveUser
    // overrides, so a role comparison here would ignore the chameleon entirely.
    const userIsSuperUser = user?.isSuperUser ?? false;
    const userRole = user?.role;
    const userEntityId = user?.entity_id;
    const userCountry = user?.country_code;
    const userOperationalCountries = user?.operationalCountries ?? [];
    const userIsMultiCountry = user?.isMultiCountry ?? false;

    const permissions = useMemo((): CountryPermissions => {
        const isSuperUser = userIsSuperUser;
        const isCustomerUser = userRole?.includes('CUSTOMER') ?? false;
        const isEditingOwnRecord = isCustomerUser && entityData?.id === userEntityId;

        return {
            isSuperUser,
            isCustomerUser,
            isEditingOwnRecord,
            canEditCountries: isSuperUser || userIsMultiCountry || isEditingOwnRecord,
            availableCountries: isSuperUser ? countries.map((c) => c.country_code) : userOperationalCountries,
        };
    }, [userRole, userEntityId, entityData?.id, userIsMultiCountry, userOperationalCountries, countries]);

    const parseOperationalCountries = useCallback(
        (operationalCountries: Array<{ country_code: string }> | null | undefined): string[] => {
            if (!operationalCountries || !Array.isArray(operationalCountries)) return [];
            return [...new Set(operationalCountries.map((c) => c.country_code))];
        },
        []
    );

    const getAvailableCountriesWithNames = useMemo(
        () => countries.filter((c) => permissions.availableCountries.includes(c.country_code)),
        [countries, permissions.availableCountries]
    );

    const initializeCountryData = useCallback(
        (clientData: EntityData = {}): EntityData => {
            let initialData = { ...clientData };

            if (clientData.operational_countries) {
                initialData.country_list = parseOperationalCountries(clientData.operational_countries);
            }

            if (!clientData.id) {
                if (!initialData.country_code && userCountry && userCountry !== 'GLOBAL') {
                    initialData.country_code = userCountry;
                }
                if (!initialData.country_list?.length) {
                    const primary = initialData.country_code || (userCountry !== 'GLOBAL' ? userCountry : null);
                    initialData.country_list = primary ? [primary] : [];
                }
                // Currency is deliberately NOT seeded from the user. `/currencies/available`
                // is chameleon-aware — it returns the *worn* entity's currency — whereas
                // `user.currency_code` is the real user's and stays so under chameleon.
                // Seeding here also won the race against that endpoint's answer, so a
                // superuser wearing an Indian broadcaster silently created customers in NZD.
                // ClientDrawer owns currency now, from the endpoint alone.
            }

            return initialData;
        },
        [parseOperationalCountries, userCountry]
    );

    const handlePrimaryCountryChange = useCallback((newPrimaryCountry: string, currentCountryList: string[] = []) => {
        let updatedList = [...currentCountryList];
        if (newPrimaryCountry && !updatedList.includes(newPrimaryCountry)) {
            updatedList = [newPrimaryCountry, ...updatedList];
        }
        return { country_code: newPrimaryCountry, country_list: updatedList };
    }, []);

    const handleOperationalCountriesChange = useCallback(
        (newCountryList: string[], primaryCountry: string, currentCountryList: string[] = []) => {
            let updated = [...newCountryList];

            if (!permissions.isSuperUser && entityData?.id) {
                const removed = currentCountryList.filter((c) => !updated.includes(c));
                if (removed.length > 0) {
                    updated = [...new Set([...updated, ...removed])];
                }
            }

            if (primaryCountry && !updated.includes(primaryCountry)) {
                updated = [primaryCountry, ...updated];
            }

            return updated;
        },
        [permissions.isSuperUser, entityData?.id]
    );

    const validateCountryData = useCallback(
        (clientData: EntityData): CountryValidationResult => {
            const errors: Record<string, string> = {};

            if (permissions.canEditCountries && !clientData.country_code) {
                errors.country_code = 'Primary country is required';
            }

            if (clientData.country_code && !permissions.isSuperUser) {
                if (!permissions.availableCountries.includes(clientData.country_code)) {
                    errors.country_code = 'Selected country is not in your operational scope';
                }
            }

            if (clientData.country_list?.length && !permissions.isSuperUser) {
                const invalid = (clientData.country_list as string[]).filter(
                    (code) => !permissions.availableCountries.includes(code)
                );
                if (invalid.length > 0) {
                    errors.operational_countries = `Cannot operate in: ${invalid.join(', ')}`;
                }
            }

            return { isValid: Object.keys(errors).length === 0, errors };
        },
        [permissions]
    );

    const formatForSubmission = useCallback(
        (clientData: EntityData): EntityData => {
            const formatted = { ...clientData };

            delete formatted.operational_countries;
            delete formatted.is_multi_country;
            delete formatted.status;
            delete formatted.taxonomy_desc;

            if (permissions.canEditCountries) {
                formatted.country_code = clientData.country_code;
                if (!formatted.country_code && userCountry && userCountry !== 'GLOBAL') {
                    formatted.country_code = userCountry;
                }
                formatted.country_list = clientData.country_list?.length
                    ? clientData.country_list
                    : formatted.country_code
                      ? [formatted.country_code]
                      : [];
            } else {
                formatted.country_code = userCountry;
                formatted.country_list = [userCountry!];
            }

            return formatted;
        },
        [permissions.canEditCountries, userCountry]
    );

    const getDefaultCountrySelections = useCallback(
        () => ({
            primaryCountry: userCountry,
            operationalCountries: userCountry ? [userCountry] : [],
        }),
        [userCountry]
    );

    const ensureCountryConsistency = useCallback((clientData: EntityData): EntityData => {
        const { country_code, country_list = [] } = clientData;
        if (country_code && !(country_list as string[]).includes(country_code)) {
            return { ...clientData, country_list: [country_code, ...(country_list as string[])] };
        }
        return clientData;
    }, []);

    return {
        permissions,
        shouldShowCountryControls: permissions.canEditCountries,
        availableCountries: getAvailableCountriesWithNames,
        initializeCountryData,
        getDefaultCountrySelections,
        handlePrimaryCountryChange,
        handleOperationalCountriesChange,
        validateCountryData,
        formatForSubmission,
        ensureCountryConsistency,
        parseOperationalCountries,
    };
};
